import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Table } from 'reactstrap';
import PageClean from '../default/PageClean';
import Alert from '../../components/Alert';
import { getCustomerTelemetry, refreshCustomerTelemetry } from '../../actions/ActionCustomer';
import { can } from '../../utils/permissions';

const TELEMETRY_WINDOW_OPTIONS = [30, 15, 7];

const CustomerTelemetryModal = ({ nCustId }) => {
  const [bIsLoading, setIsLoading] = useState(true);
  const [bIsRefreshing, setIsRefreshing] = useState(false);
  const [aErrors, setErrors] = useState([]);
  const [oTelemetry, setTelemetry] = useState(null);
  const [nWindowDays, setWindowDays] = useState(30);

  const loadTelemetry = useCallback(async () => {
    setIsLoading(true);
    setErrors([]);

    try {
      const oResult = await getCustomerTelemetry(nCustId, nWindowDays);
      setTelemetry(oResult);
    } catch (oError) {
      setErrors(oError.aMsgErrors || ['Erro ao carregar telemetria do cliente.']);
    } finally {
      setIsLoading(false);
    }
  }, [nCustId, nWindowDays]);

  const refreshTelemetry = useCallback(async () => {
    if (!nCustId || bIsRefreshing) return;

    setIsRefreshing(true);
    setErrors([]);

    try {
      const oResult = await refreshCustomerTelemetry(nCustId, nWindowDays);
      setTelemetry(oResult);
    } catch (oError) {
      setErrors(oError.aMsgErrors || ['Erro ao atualizar telemetria do cliente.']);
    } finally {
      setIsRefreshing(false);
    }
  }, [bIsRefreshing, nCustId, nWindowDays]);

  const clearError = (cError) => {
    setErrors((aCurrentErrors) => aCurrentErrors.filter((cCurrentError) => cCurrentError !== cError));
  };

  useEffect(() => {
    if (!nCustId) return;
    loadTelemetry();
  }, [loadTelemetry, nCustId]);

  const aSummaryCards = useMemo(() => {
    if (!oTelemetry) return [];

    const oAccess = oTelemetry.oAccess || {};
    const oCustomer = oTelemetry.oCustomer || {};
    const oPlan = oTelemetry.oPlan || {};

    return [
      {
        cLabel: 'Ultimo acesso',
        cValue: formatDateTime(oAccess.cLastAccessAt) || 'Sem acesso',
      },
      {
        cLabel: 'Dias sem acesso',
        cValue: oAccess.nDaysWithoutAccess === null || typeof oAccess.nDaysWithoutAccess === 'undefined'
          ? '-'
          : `${oAccess.nDaysWithoutAccess} dia(s)`,
      },
      {
        cLabel: 'Usuarios contratados',
        cValue: formatNumber(oPlan.plan_nusers),
      },
      {
        cLabel: 'Usuarios ativos',
        cValue: oAccess.nActiveUsers || 0,
      },
      {
        cLabel: 'Usuarios sem acesso',
        cValue: oAccess.nUsersNeverAccessed || 0,
      },
      {
        cLabel: 'Modulos contratados',
        cValue: (oTelemetry.aModules || []).length,
      },
      {
        cLabel: 'Inicio da conta',
        cValue: formatDate(oCustomer.cust_dcreated) || '-',
      },
      {
        cLabel: 'Vencimento do plano',
        cValue: formatDate(oPlan.plan_dend) || '-',
      },
    ];
  }, [oTelemetry]);

  return (
    <PageClean bLoading={bIsLoading}>
      <div style={{ paddingRight: '10px' }}>
        {aErrors.map((cError) => (
          <Alert key={cError} type="error" onCloseAlert={() => clearError(cError)}>
            {cError}
          </Alert>
        ))}

        {oTelemetry && (
          <>
            <CustomerSummary
              oTelemetry={oTelemetry}
              aSummaryCards={aSummaryCards}
              nWindowDays={nWindowDays}
              onWindowDaysChange={setWindowDays}
              bIsRefreshing={bIsRefreshing}
              onRefreshTelemetry={refreshTelemetry}
            />
            <HealthScorePanel oHealthScore={oTelemetry.oHealthScore30d || {}} />
            <TelemetryHistoryTable aHistory={(oTelemetry.oSnapshot || {}).aHistory || []} />
          </>
        )}
      </div>
    </PageClean>
  );
};

const CustomerSummary = ({
  oTelemetry,
  aSummaryCards,
  nWindowDays,
  onWindowDaysChange,
  bIsRefreshing,
  onRefreshTelemetry,
}) => {
  const oCustomer = oTelemetry.oCustomer || {};
  const oTenant = oTelemetry.oTenant || {};
  const oSnapshot = oTelemetry.oSnapshot || {};
  const aAlerts = oTelemetry.aAlerts || [];
  const bCanEditCustomer = can('edit-customer');
  const bCanRefresh = bCanEditCustomer && !!oSnapshot.bCanRefresh && !bIsRefreshing;
  const cRefreshTitle = getRefreshTitle(oSnapshot, bCanEditCustomer);

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <strong>{oCustomer.cust_cfantasy_name || oCustomer.cust_cname}</strong>
        <div style={{ color: '#6c7685', marginTop: '4px' }}>
          ID {oCustomer.cust_nid} {oCustomer.cust_cemail ? `- ${oCustomer.cust_cemail}` : ''}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '10px',
        marginBottom: '16px',
      }}>
        {aSummaryCards.map((oCard) => (
          <div
            key={oCard.cLabel}
            style={{
              border: '1px solid #dfe6ef',
              borderRadius: '6px',
              padding: '12px',
              minHeight: '74px',
            }}
          >
            <div style={{ color: '#6c7685', fontSize: '12px', marginBottom: '6px' }}>
              {oCard.cLabel}
            </div>
            <strong>{oCard.cValue}</strong>
          </div>
        ))}
      </div>

      <div
        style={{
          border: '1px solid #dfe6ef',
          borderRadius: '6px',
          marginBottom: '16px',
          padding: '10px 12px',
          background: oSnapshot.bFromSnapshot ? '#eef4ff' : '#fff6e8',
          color: oSnapshot.bFromSnapshot ? '#24549c' : '#9a5b00',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <strong>Origem dos dados: </strong>
            {oSnapshot.bFromSnapshot
              ? `snapshot de ${nWindowDays} dias coletado em ${formatDateTime(oSnapshot.cCollectedAt) || '-'}`
              : 'snapshot ainda indisponivel; exibindo dados basicos do cliente'}
            {oSnapshot.cRefreshAvailableAt && !oSnapshot.bCanRefresh && (
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Atualizacao liberada em {formatDateTime(oSnapshot.cRefreshAvailableAt)}.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ margin: 0, color: '#394657', fontSize: '12px', fontWeight: 700 }}>
              Periodo
              <select
                value={nWindowDays}
                onChange={(oEvent) => onWindowDaysChange(Number(oEvent.target.value))}
                disabled={bIsRefreshing}
                style={{
                  display: 'block',
                  minWidth: '108px',
                  marginTop: '4px',
                  border: '1px solid #d1dbe8',
                  borderRadius: '4px',
                  padding: '6px 8px',
                  background: '#fff',
                }}
              >
                {TELEMETRY_WINDOW_OPTIONS.map((nOption) => (
                  <option key={nOption} value={nOption}>
                    {nOption} dias
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onRefreshTelemetry}
              disabled={!bCanRefresh}
              title={cRefreshTitle}
              style={{ margin: 0 }}
            >
              {bIsRefreshing ? 'Atualizando...' : 'Atualizar telemetria'}
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #dfe6ef',
          borderRadius: '6px',
          marginBottom: '16px',
          padding: '10px 12px',
          background: oTenant.bAvailable ? '#eefaf4' : '#fff6e8',
          color: oTenant.bAvailable ? '#1e7e4f' : '#9a5b00',
        }}
      >
        <strong>Status tenant: </strong>
        {oTenant.bAvailable ? 'metricas carregadas' : oTenant.cWarning || 'metricas indisponiveis'}
      </div>

      {aAlerts.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          {aAlerts.map((cAlert) => (
            <Alert key={cAlert} type="warning" onCloseAlert={() => {}}>
              {cAlert}
            </Alert>
          ))}
        </div>
      )}
    </>
  );
};

const TelemetryHistoryTable = ({ aHistory }) => {
  if (!aHistory.length) {
    return null;
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>Historico de coletas</h3>
      <Table responsive striped size="sm">
        <thead>
          <tr>
            <th>Inicio</th>
            <th>Origem</th>
            <th>Status</th>
            <th>Duracao</th>
            <th>Mensagem</th>
          </tr>
        </thead>
        <tbody>
          {aHistory.map((oHistory) => (
            <tr key={oHistory.nId}>
              <td>{formatDateTime(oHistory.cStartedAt) || '-'}</td>
              <td>{getSourceLabel(oHistory.cSource)}</td>
              <td>{getStatusLabel(oHistory.cStatus)}</td>
              <td>{formatDuration(oHistory.nDurationSeconds)}</td>
              <td>{oHistory.cWarning || oHistory.cError || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const HealthScorePanel = ({ oHealthScore }) => {
  const [cActiveModule, setActiveModule] = useState('operational-panel');
  const nWindowDays = oHealthScore.nWindowDays || 30;
  const oSystem = oHealthScore.oSystem || {};
  const oOperationalPanel = oHealthScore.oOperationalPanel || {};
  const oIndicator = oHealthScore.oIndicator || {};
  const oRiskManagement = oHealthScore.oRiskManagement || {};
  const oNonConformities = oHealthScore.oNonConformities || {};
  const oDocuments = oHealthScore.oDocuments || {};
  const oQualityPanel = oHealthScore.oQualityPanel || {};
  const oAuditManagement = oHealthScore.oAuditManagement || {};
  const oCiq = oHealthScore.oCiq || {};
  const aModuleTabs = [
    {
      cKey: 'operational-panel',
      cTitle: 'Painel Operacional',
      bHadActivity: oOperationalPanel.bHadActivity,
      aRows: [
        ['Atividades criadas', formatNumber(oOperationalPanel.nActivitiesCreated)],
        ['Atividades editadas', formatNumber(oOperationalPanel.nActivitiesUpdated)],
        ['Atividades excluidas', formatNumber(oOperationalPanel.nActivitiesDeleted)],
        ['Movimentacoes registradas', formatNumber(oOperationalPanel.nActivityEvents)],
        ['Atividades concluidas', formatNumber(oOperationalPanel.nCompletedActivities)],
        ['% concluidas sobre criadas', formatPercent(oOperationalPanel.nCompletedActivitiesPercent)],
        ['Automacoes com sinais de uso', formatNumber(oOperationalPanel.nAutomationsUsed)],
        ['Ultima movimentacao', formatDateTime(oOperationalPanel.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'ciq',
      cTitle: 'CIQ',
      bHadActivity: oCiq.bHadActivity,
      aRows: [
        ['Corridas lancadas', formatNumber(oCiq.nRunsLaunched)],
        ['Media diaria de lancamentos', formatNumber(oCiq.nDailyLaunchAverage)],
        ['Corridas concluidas', formatNumber(oCiq.nCompletedRuns)],
        ['% corridas concluidas', formatPercent(oCiq.nCompletedRunsPercent)],
        ['Concluidas aprovadas', formatNumber(oCiq.nApprovedCompletedRuns)],
        ['% concluidas aprovadas', formatPercent(oCiq.nApprovedCompletedRunsPercent)],
        ['Concluidas reprovadas', formatNumber(oCiq.nRejectedCompletedRuns)],
        ['% concluidas reprovadas', formatPercent(oCiq.nRejectedCompletedRunsPercent)],
        ['Corridas pendentes', formatNumber(oCiq.nPendingRuns)],
        ['Corridas editadas', formatNumber(oCiq.nRunsUpdated)],
        ['Corridas excluidas', formatNumber(oCiq.nRunsDeleted)],
        ['Eventos regra Westgard', formatNumber(oCiq.nWestgardRuleEvents)],
        ['Lancamentos de media manual', formatNumber(oCiq.nManualAverageLaunches)],
        ['Movimentacoes registradas', formatNumber(oCiq.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oCiq.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'documents',
      cTitle: 'Documentos',
      bHadActivity: oDocuments.bHadActivity,
      aRows: [
        ['Houve publicacao', formatBooleanMetric(oDocuments.bHadPublication)],
        ['Novos documentos', formatNumber(oDocuments.nNewDocuments)],
        ['Documentos editados', formatNumber(oDocuments.nDocumentsUpdated)],
        ['Documentos excluidos', formatNumber(oDocuments.nDocumentsDeleted)],
        ['Novas versoes', formatNumber(oDocuments.nNewVersions)],
        ['Versoes editadas', formatNumber(oDocuments.nVersionsUpdated)],
        ['Versoes excluidas', formatNumber(oDocuments.nVersionsDeleted)],
        ['Versoes publicadas', formatNumber(oDocuments.nPublishedVersions)],
        ['Leituras confirmadas', formatNumber(oDocuments.nConfirmedReadings)],
        ['Movimentacoes registradas', formatNumber(oDocuments.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oDocuments.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'quality-panel',
      cTitle: 'Painel da Qualidade',
      bHadActivity: oQualityPanel.bHadActivity,
      aRows: [
        ['Normas criadas', formatNumber(oQualityPanel.nNormsCreated)],
        ['Normas editadas', formatNumber(oQualityPanel.nNormsUpdated)],
        ['Normas excluidas', formatNumber(oQualityPanel.nNormsDeleted)],
        ['Requisitos criados', formatNumber(oQualityPanel.nRequisitesCreated)],
        ['Requisitos editados', formatNumber(oQualityPanel.nRequisitesUpdated)],
        ['Requisitos excluidos', formatNumber(oQualityPanel.nRequisitesDeleted)],
        ['Requisitos auditados criados', formatNumber(oQualityPanel.nAuditRequisitesCreated)],
        ['Requisitos auditados editados', formatNumber(oQualityPanel.nAuditRequisitesUpdated)],
        ['Requisitos auditados excluidos', formatNumber(oQualityPanel.nAuditRequisitesDeleted)],
        ['Requisitos concluidos', formatNumber(oQualityPanel.nCompletedRequisites)],
        ['Requisitos concluidos com evidencia', formatPercent(oQualityPanel.nCompletedRequisitesWithEvidencePercent)],
        ['Evidencias criadas', formatNumber(oQualityPanel.nEvidencesCreated)],
        ['Evidencias editadas', formatNumber(oQualityPanel.nEvidencesUpdated)],
        ['Evidencias excluidas', formatNumber(oQualityPanel.nEvidencesDeleted)],
        ['Movimentacoes registradas', formatNumber(oQualityPanel.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oQualityPanel.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'audit-management',
      cTitle: 'Auditorias',
      bHadActivity: oAuditManagement.bHadActivity,
      aRows: [
        ['Auditorias criadas', formatNumber(oAuditManagement.nAuditsCreated)],
        ['Auditorias editadas', formatNumber(oAuditManagement.nAuditsUpdated)],
        ['Auditorias excluidas', formatNumber(oAuditManagement.nAuditsDeleted)],
        ['Auditorias concluidas', formatNumber(oAuditManagement.nCompletedAudits)],
        ['% concluidas sobre criadas', formatPercent(oAuditManagement.nCompletedAuditsPercent)],
        ['Evidencias criadas', formatNumber(oAuditManagement.nEvidencesCreated)],
        ['Evidencias editadas', formatNumber(oAuditManagement.nEvidencesUpdated)],
        ['Evidencias excluidas', formatNumber(oAuditManagement.nEvidencesDeleted)],
        ['Movimentacoes registradas', formatNumber(oAuditManagement.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oAuditManagement.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'indicator',
      cTitle: 'Indicadores',
      bHadActivity: oIndicator.bHadActivity,
      aRows: [
        ['Abas criadas', formatNumber(oIndicator.nTabsCreated)],
        ['Abas editadas', formatNumber(oIndicator.nTabsUpdated)],
        ['Abas excluidas', formatNumber(oIndicator.nTabsDeleted)],
        ['Indicadores criados', formatNumber(oIndicator.nIndicatorsCreated)],
        ['Indicadores editados', formatNumber(oIndicator.nIndicatorsUpdated)],
        ['Indicadores excluidos', formatNumber(oIndicator.nIndicatorsDeleted)],
        ['Blocos criados', formatNumber(oIndicator.nBlocksCreated)],
        ['Blocos editados', formatNumber(oIndicator.nBlocksUpdated)],
        ['Blocos excluidos', formatNumber(oIndicator.nBlocksDeleted)],
        ['Marcacoes criadas', formatNumber(oIndicator.nMarksCreated)],
        ['Marcacoes editadas', formatNumber(oIndicator.nMarksUpdated)],
        ['Marcacoes excluidas', formatNumber(oIndicator.nMarksDeleted)],
        ['Media diaria de marcacoes', formatNumber(oIndicator.nDailyMarkAverage)],
        ['Movimentacoes registradas', formatNumber(oIndicator.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oIndicator.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'risk-management',
      cTitle: 'Riscos',
      bHadActivity: oRiskManagement.bHadActivity,
      aRows: [
        ['Riscos criados', formatNumber(oRiskManagement.nActivitiesCreated)],
        ['Riscos editados', formatNumber(oRiskManagement.nActivitiesUpdated)],
        ['Riscos excluidos', formatNumber(oRiskManagement.nActivitiesDeleted)],
        ['Riscos concluidos', formatNumber(oRiskManagement.nCompletedActivities)],
        ['% concluidos sobre criados', formatPercent(oRiskManagement.nCompletedActivitiesPercent)],
        ['Movimentacoes registradas', formatNumber(oRiskManagement.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oRiskManagement.cLastMovementAt) || '-'],
      ],
    },
    {
      cKey: 'non-conformities',
      cTitle: 'Nao Conformidades',
      bHadActivity: oNonConformities.bHadActivity,
      aRows: [
        ['Nao conformidades criadas', formatNumber(oNonConformities.nActivitiesCreated)],
        ['Nao conformidades editadas', formatNumber(oNonConformities.nActivitiesUpdated)],
        ['Nao conformidades excluidas', formatNumber(oNonConformities.nActivitiesDeleted)],
        ['Itens concluidos', formatNumber(oNonConformities.nCompletedActivities)],
        ['% concluidos sobre criados', formatPercent(oNonConformities.nCompletedActivitiesPercent)],
        ['Movimentacoes registradas', formatNumber(oNonConformities.nActivityEvents)],
        ['Ultima movimentacao', formatDateTime(oNonConformities.cLastMovementAt) || '-'],
      ],
    },
  ];
  const oActiveModule = aModuleTabs.find((oModule) => oModule.cKey === cActiveModule) || aModuleTabs[0];

  return (
    <div style={{ marginBottom: '16px' }}>
      <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>
        Acompanhamento de Sucesso de Cliente - ultimos {nWindowDays} dias
      </h3>

      <div style={{ color: '#6c7685', marginBottom: '10px' }}>
        Periodo: {formatDateTime(oHealthScore.cStartAt) || '-'} ate {formatDateTime(oHealthScore.cEndAt) || '-'}
      </div>

      <TelemetrySection
        cTitle="Sistema / Geral"
        aRows={[
          ['Usuarios que logaram', formatNumber(oSystem.nUsersLogged)],
          ['Media de acesso diario', formatNumber(oSystem.nDailyAccessAverage)],
        ]}
      />

      <UsersLoggedTable aUsers={oSystem.aUsersLogged || []} nWindowDays={nWindowDays} />

      <ModuleActivityTabs
        aModules={aModuleTabs}
        cActiveModule={oActiveModule.cKey}
        onSelectModule={setActiveModule}
      />

      <TelemetrySection cTitle={oActiveModule.cTitle} aRows={oActiveModule.aRows} />
    </div>
  );
};

const ModuleActivityTabs = ({ aModules, cActiveModule, onSelectModule }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: '8px',
      marginBottom: '14px',
    }}
  >
    {aModules.map((oModule) => {
      const oTone = getActivityTone(oModule.bHadActivity);
      const bIsActive = oModule.cKey === cActiveModule;

      return (
        <button
          type="button"
          key={oModule.cKey}
          onClick={() => onSelectModule(oModule.cKey)}
          style={{
            border: `1px solid ${bIsActive ? '#2f80ed' : oTone.cBorder}`,
            borderRadius: '6px',
            background: oTone.cBackground,
            color: oTone.cColor,
            padding: '9px 10px',
            minHeight: '58px',
            textAlign: 'left',
            cursor: 'pointer',
            boxShadow: bIsActive ? '0 0 0 2px rgba(47, 128, 237, 0.15)' : 'none',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 700 }}>{oModule.cTitle}</div>
        </button>
      );
    })}
  </div>
);

const TelemetrySection = ({ cTitle, aRows }) => (
  <section
    style={{
      border: '1px solid #dfe6ef',
      borderRadius: '6px',
      marginBottom: '14px',
      overflow: 'hidden',
      background: '#ffffff',
    }}
  >
    <h4
      style={{
        fontSize: '14px',
        margin: 0,
        padding: '10px 12px',
        color: '#394657',
        background: '#f7f9fc',
        borderBottom: '1px solid #dfe6ef',
      }}
    >
      {cTitle}
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '8px',
        padding: '10px 12px',
      }}
    >
      {aRows.map(([cLabel, cValue]) => (
        <div
          key={cLabel}
          style={{
            border: '1px solid #e8edf4',
            borderRadius: '4px',
            padding: '10px 12px',
            minHeight: '70px',
            background: '#fbfcfe',
          }}
        >
          <div style={{ color: '#6c7685', fontSize: '12px', marginBottom: '5px' }}>
            {cLabel}
          </div>
          <MetricValue cValue={cValue} />
        </div>
      ))}
    </div>
  </section>
);

const MetricValue = ({ cValue }) => {
  const cText = cValue === null || typeof cValue === 'undefined' ? '-' : String(cValue);
  const bStatusValue = ['Sim', 'Nao', 'Sem dado'].includes(cText);
  const bNumericValue = cText !== '-' && !bStatusValue && cText.length <= 12;

  if (bStatusValue) {
    const bIsPositive = cText === 'Sim';

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '999px',
          padding: '3px 9px',
          fontSize: '12px',
          fontWeight: 700,
          background: bIsPositive ? '#eefaf4' : '#f4f5f7',
          color: bIsPositive ? '#1e7e4f' : '#596575',
        }}
      >
        {cText}
      </span>
    );
  }

  return (
    <strong
      style={{
        color: '#1f2d3d',
        fontSize: bNumericValue ? '20px' : '14px',
        lineHeight: bNumericValue ? '24px' : '18px',
        fontWeight: 700,
      }}
    >
      {cText}
    </strong>
  );
};

const getActivityTone = (bHadActivity) => {
  if (bHadActivity === null || typeof bHadActivity === 'undefined') {
    return {
      cBackground: '#f7f9fc',
      cBorder: '#dfe6ef',
      cColor: '#6c7685',
    };
  }

  if (bHadActivity) {
    return {
      cBackground: '#eefaf4',
      cBorder: '#bde7d0',
      cColor: '#1e7e4f',
    };
  }

  return {
    cBackground: '#f4f5f7',
    cBorder: '#dfe3e8',
    cColor: '#596575',
  };
};

const UsersLoggedTable = ({ aUsers, nWindowDays }) => (
  <div style={{ marginBottom: '14px' }}>
    <h4 style={{ fontSize: '14px', marginBottom: '6px', color: '#394657' }}>
      Usuarios que acessaram nos ultimos {nWindowDays} dias
    </h4>
    <Table responsive striped size="sm">
      <thead>
        <tr>
          <th>Usuario</th>
          <th>Perfil</th>
          <th>Ultimo acesso no periodo</th>
        </tr>
      </thead>
      <tbody>
        {aUsers.map((oUser) => (
          <tr key={oUser.user_nid}>
            <td>{oUser.user_cname || '-'}</td>
            <td>{oUser.bIsAdmin ? 'Administrador' : 'Usuario'}</td>
            <td>{formatDateTime(oUser.cLastAccessAt) || '-'}</td>
          </tr>
        ))}
        {aUsers.length === 0 && (
          <tr>
            <td colSpan="3" className="centered-text">
              Nenhum usuario acessou nos ultimos {nWindowDays} dias.
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </div>
);

const getRefreshTitle = (oSnapshot, bCanEditCustomer) => {
  if (!bCanEditCustomer) {
    return 'Sem permissao para atualizar telemetria.';
  }

  if (oSnapshot.cRefreshBlockedReason) {
    return oSnapshot.cRefreshBlockedReason;
  }

  if (oSnapshot.cRefreshAvailableAt && !oSnapshot.bCanRefresh) {
    return `Atualizacao liberada em ${formatDateTime(oSnapshot.cRefreshAvailableAt)}.`;
  }

  return 'Atualizar snapshot de telemetria deste cliente.';
};

const getStatusLabel = (cStatus) => {
  const oLabels = {
    running: 'Em execucao',
    success: 'Sucesso',
    failed: 'Falha',
    pending: 'Pendente',
  };

  return oLabels[cStatus] || cStatus || '-';
};

const getSourceLabel = (cSource) => {
  const oLabels = {
    manual: 'Manual',
    schedule: 'Agendada',
  };

  return oLabels[cSource] || cSource || '-';
};

const formatDuration = (nSeconds) => {
  if (nSeconds === null || typeof nSeconds === 'undefined') return '-';
  if (nSeconds < 60) return `${nSeconds}s`;

  const nMinutes = Math.floor(nSeconds / 60);
  const nRemainingSeconds = nSeconds % 60;

  return `${nMinutes}m ${nRemainingSeconds}s`;
};

const formatBooleanMetric = (bValue) => {
  if (bValue === null || typeof bValue === 'undefined') return 'Sem dado';
  return bValue ? 'Sim' : 'Nao';
};

const formatNumber = (nValue) => {
  if (nValue === null || typeof nValue === 'undefined') return '-';
  return nValue;
};

const formatPercent = (nValue) => {
  if (nValue === null || typeof nValue === 'undefined') return '-';
  return `${nValue}%`;
};

const formatDateTime = (cDateTime) => {
  if (!cDateTime) return null;
  return moment.parseZone(cDateTime).format('DD/MM/YYYY HH:mm');
};

const formatDate = (cDateTime) => {
  if (!cDateTime) return null;
  return moment.parseZone(cDateTime).format('DD/MM/YYYY');
};

export default CustomerTelemetryModal;
